import type { AccessEndedReason } from "../auth/trialGate";

/**
 * The "what happened to my data" sentence on the access-ended screen (KEY-667, KEY-912).
 *
 * With data retention off — how the platform ships — "nothing has been deleted" is simply true and stays true,
 * so the screen says it exactly as before. Once retention is on for this organisation that promise has an end
 * date, and the screen must not make it unconditionally: it says the data is still here, for a limited time,
 * and names the date once the countdown emails have given one.
 */
export interface KeptCopy {
  /** The bold lead. */
  lead: string;
  /** The rest of the paragraph. */
  rest: string;
}

export function keptCopy(
  retentionOn: boolean,
  deletionAtUtc: string | null | undefined,
  organisationName: string | null | undefined,
  reason: AccessEndedReason = "trial",
): KeptCopy {
  const forOrg = organisationName ? ` for ${organisationName}` : "";
  const cancelled = reason === "cancelled";

  if (!retentionOn) {
    // KEY-912: a cancelled customer is told the same thing in Tim's words — safe, and how to pick it back up.
    if (cancelled) {
      return {
        lead: "Your dials and data are safe.",
        rest: "Subscribe again to pick up where you left off.",
      };
    }
    return {
      lead: "Nothing has been deleted.",
      rest: `Every dial, document and update${forOrg} is exactly where you left it. Subscribe below and it is all there again straight away.`,
    };
  }

  if (deletionAtUtc) {
    // Written the way the countdown email writes it ("Fri 30 October 2026"), so the two agree word for word.
    const parts = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })
      .formatToParts(new Date(deletionAtUtc));
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
    const date = `${part("weekday")} ${part("day")} ${part("month")} ${part("year")}`;
    return {
      lead: `Everything is kept until ${date}.`,
      rest: `Every dial, document and update${forOrg} is still here, but after that date it will be permanently deleted. ${cancelled ? "Subscribe again" : "Subscribe below"} before then and it is all there again straight away.`,
    };
  }

  return {
    lead: "Everything is still here — for now.",
    rest: `Every dial, document and update${forOrg} is kept for a limited time after ${cancelled ? "a subscription" : "a trial"} ends, and we'll email you the date before anything is deleted. ${cancelled ? "Subscribe again" : "Subscribe below"} and it is all there again straight away.`,
  };
}

/** The rest of the screen's words, by why access ended (KEY-912). */
export interface EndedCopy {
  heading: string;
  /** The line under the reassurance about money. Null says nothing — a cancelled customer did pay, so the trial's
   * "we never took a card" would be untrue. */
  paymentNote: string | null;
  subscribeLabel: string;
}

/**
 * The heading, the money line and the button, for the reason access ended.
 *
 * A relative on somebody's family pack (KEY-651) is told whose place it was, whatever the reason: the pack ended,
 * or the buyer took them off it — either way it is not a trial of theirs that ran out, nor a plan they cancelled.
 * The trial's own wording is exactly what it was before KEY-912.
 */
export function endedCopy(reason: AccessEndedReason | null, sponsoredBy: string | null): EndedCopy {
  if (sponsoredBy) {
    return {
      heading: `Your place on ${sponsoredBy}'s family plan has ended`,
      paymentNote: `${sponsoredBy} paid for your dial until now, so nothing has been charged to you. Subscribing takes a card and starts a plan of your own straight away.`,
      subscribeLabel: "Subscribe and pick up where I left off",
    };
  }
  if (reason === "cancelled") {
    return {
      heading: "Your subscription has ended",
      paymentNote: null,
      subscribeLabel: "Subscribe again",
    };
  }
  return {
    heading: "Your free trial has ended",
    paymentNote: "We never took a card for the trial, so nothing has been charged. Subscribing takes a card and starts your plan straight away.",
    subscribeLabel: "Subscribe and pick up where I left off",
  };
}
